export type RiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';

export type Gender = 'MALE' | 'FEMALE' | 'OTHER' | 'NOT_INFORMED';

export type PatientProgramStatus = 'ELIGIBLE' | 'CAPTATION' | 'ACTIVE' | 'INACTIVE' | 'DISCHARGED';

export type InactivationReason =
  | 'DECEASED'
  | 'DELINQUENT'
  | 'VOLUNTARY_EXIT'
  | 'NO_CONTACT'
  | 'TRANSFERRED'
  | 'PLAN_CANCELLED'
  | 'OTHER';

export const InactivationReasonLabels: Record<InactivationReason, string> = {
  DECEASED: 'Óbito',
  DELINQUENT: 'Inadimplente',
  VOLUNTARY_EXIT: 'Saída Voluntária',
  NO_CONTACT: 'Sem Contato',
  TRANSFERRED: 'Transferido',
  PLAN_CANCELLED: 'Plano Cancelado',
  OTHER: 'Outro',
};

export const ProgramStatusLabels: Record<PatientProgramStatus, string> = {
  ELIGIBLE: 'Elegível',
  CAPTATION: 'Em Captação',
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  DISCHARGED: 'Desligado (Alta)',
};

export interface Address {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Patient {
  id: string;
  name: string;
  cpf: string;
  birthDate: string;
  age: number;
  gender: Gender;
  phone?: string;
  email?: string;
  riskLevel?: RiskLevel;
  activeProblemsCount: number;
  programStatus?: PatientProgramStatus;
  programStatusLabel?: string;
  createdAt: string;
}

export interface PatientProblem {
  id: string;
  cidCode: string;
  description: string;
  status: 'ACTIVE' | 'RESOLVED' | 'INACTIVE';
  onsetDate?: string;
  resolutionDate?: string;
  notes?: string;
  createdAt: string;
}

export interface PatientMedication {
  id: string;
  medicationName: string;
  activeIngredient?: string;
  dosage: string;
  frequency: string;
  route?: string;
  startDate?: string;
  endDate?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'COMPLETED' | 'CANCELLED';
  prescribedByName?: string;
  notes?: string;
  createdAt?: string;
  // Campos de suspensão
  suspendedAt?: string;
  suspendedReason?: string;
  suspendedByName?: string;
  // Campos de reativação
  reactivatedAt?: string;
  reactivatedReason?: string;
  reactivatedByName?: string;
}

export interface PatientAllergy {
  id: string;
  allergen: string;
  allergenType?: 'MEDICATION' | 'FOOD' | 'ENVIRONMENTAL' | 'OTHER';
  reaction?: string;
  severity?: 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING';
  confirmed: boolean;
}

export interface PatientDetail extends Patient {
  externalId?: string;
  address?: Address;
  problems: PatientProblem[];
  medications: PatientMedication[];
  allergies: PatientAllergy[];
  careLines: PatientCareLine[];
  // Campos de programa e inativação
  programStatus?: PatientProgramStatus;
  inactivationReason?: InactivationReason;
  inactivationNotes?: string;
  inactivatedAt?: string;
  inactivatedBy?: string;
  deceasedAt?: string;
  canReceiveCommunications?: boolean;
}

export interface PatientCareLine {
  id: string;
  careLineId: string;
  careLineName: string;
  careLineCode: string;
  enrolledAt: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'DISCHARGED';
  currentPhase?: string;
  currentPhaseName?: string;
}

export interface PatientFilters {
  search?: string;
  riskLevel?: RiskLevel;
  careLineId?: string;
  programStatus?: PatientProgramStatus;
}

// Response type for patient list (simplified version for listings)
export interface PatientListResponse {
  id: string;
  name: string;
  cpf: string;
  age: number;
  phone?: string;
  riskLevel?: RiskLevel;
  activeProblemsCount: number;
}

export interface CreatePatientRequest {
  cpf: string;
  name: string;
  birthDate: string;
  gender: Gender;
  phone: string;
  email: string;
  address: Address;
}

export interface UpdatePatientRequest {
  name: string;
  birthDate: string;
  gender?: Gender;
  phone: string;
  email: string;
  address: Address;
}

// Timeline Types
export type TimelineEventType =
  | 'CARE_PLAN'
  | 'CARE_PLAN_STATUS_CHANGE'
  | 'RISK_ASSESSMENT'
  | 'QUESTIONNAIRE_RESPONSE'
  | 'PROBLEM_ADDED'
  | 'PROBLEM_RESOLVED'
  | 'MEDICATION_STARTED'
  | 'MEDICATION_SUSPENDED'
  | 'MEDICATION_REACTIVATED'
  | 'MEDICATION_STOPPED'
  | 'CARE_LINE_ENROLLMENT'
  | 'CARE_LINE_DISCHARGE'
  | 'ALERT';

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  typeLabel: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  eventDate: string;
  createdByName?: string;
  metadata?: Record<string, unknown>;
}

// Request types for patient management
export interface CreateProblemRequest {
  cidCode?: string;
  description: string;
  onsetDate?: string;
  notes?: string;
}

export interface UpdateProblemRequest {
  cidCode?: string;
  description?: string;
  status?: 'ACTIVE' | 'RESOLVED' | 'INACTIVE';
  onsetDate?: string;
  resolutionDate?: string;
  notes?: string;
}

export interface CreateMedicationRequest {
  medicationName: string;
  activeIngredient?: string;
  dosage: string;
  frequency: string;
  route?: string;
  startDate?: string;
  notes?: string;
}

export interface UpdateMedicationRequest {
  medicationName?: string;
  activeIngredient?: string;
  dosage?: string;
  frequency?: string;
  route?: string;
  startDate?: string;
  endDate?: string;
  status?: 'ACTIVE' | 'SUSPENDED' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
}

export interface SuspendMedicationRequest {
  reason: string;
}

export interface ReactivateMedicationRequest {
  reason?: string;
}

export interface CreateAllergyRequest {
  allergen: string;
  allergenType?: 'MEDICATION' | 'FOOD' | 'ENVIRONMENTAL' | 'OTHER';
  reaction?: string;
  severity?: 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING';
  confirmed?: boolean;
}

export interface UpdateAllergyRequest {
  allergen?: string;
  allergenType?: 'MEDICATION' | 'FOOD' | 'ENVIRONMENTAL' | 'OTHER';
  reaction?: string;
  severity?: 'MILD' | 'MODERATE' | 'SEVERE' | 'LIFE_THREATENING';
  confirmed?: boolean;
}

// Inactivation Request Types
export interface InactivatePatientRequest {
  reason: InactivationReason;
  notes?: string;
}

export interface MarkDeceasedRequest {
  deceasedAt?: string;
  notes?: string;
}

export interface DischargePatientRequest {
  notes?: string;
}

export interface InactivationStats {
  totalInactive: number;
  totalDeceased: number;
  totalDischarged: number;
  countsByReason: Record<InactivationReason, number>;
}

export interface ProgramStatusCounts {
  total: number;
  eligible: number;
  captation: number;
  active: number;
  inactive: number;
  discharged: number;
}
